import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

interface AIEnrichButtonProps {
  companyId: string;
  companyName: string;
  websiteUrl?: string | null;
  onEnriched?: () => void;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'icon';
}

export function AIEnrichButton({
  companyId,
  companyName,
  websiteUrl,
  onEnriched,
  variant = 'outline',
  size = 'sm'
}: AIEnrichButtonProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleEnrich = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('enrich-company', {
        body: { companyId, companyName, websiteUrl }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: 'Company Enriched',
          description: `Added AI-sourced data for ${companyName}${data.addedPeople?.length ? ` including ${data.addedPeople.length} key people` : ''}`,
        });
        onEnriched?.();
      } else {
        throw new Error(data.error || 'Enrichment failed');
      }
    } catch (error) {
      console.error('Enrich error:', error);
      toast({
        title: 'Enrichment Failed',
        description: error instanceof Error ? error.message : 'Failed to enrich company',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleEnrich}
      disabled={isLoading}
      className="gap-1.5"
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Sparkles className="h-4 w-4" />
      )}
      {size !== 'icon' && (isLoading ? 'Enriching...' : 'AI Enrich')}
    </Button>
  );
}
